import { UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { useIntl } from "react-intl"
import { carrotClient } from "#/utils/CarrotClient"
import { successToast } from "#/components/toasts"
import toast from "react-hot-toast"

export function useUpdateAccountSettings(
  onPartnerFieldsUpdated?: () => void,
  onSaveStarted?: () => void,
  carrotConnectEnabled?: boolean
): UseMutationResult {
  const {
    refreshUserInfo,
    legallyMarried: currentLegallyMarried,
    partnerAsDependent: currentPartnerAsDependent
  } = useCurrentUser()
  const intl = useIntl()
  const queryClient = useQueryClient()

  const shouldShowToast = (userInfo: Record<string, any>): boolean => {
    const legallyMarriedChanged = currentLegallyMarried === null && userInfo.legallyMarried !== null
    const partnerAsDependentChanged = currentPartnerAsDependent === null && userInfo.partnerAsDependent !== null

    if (!carrotConnectEnabled) {
      return true
    }

    // Don't show toast when these specific fields change
    return !(legallyMarriedChanged || partnerAsDependentChanged)
  }

  const hasPartnerFieldsChanged = (userInfo: Record<string, any>): boolean => {
    const legallyMarriedChanged = currentLegallyMarried === null && userInfo.legallyMarried !== null
    const partnerAsDependentChanged = currentPartnerAsDependent === null && userInfo.partnerAsDependent !== null

    return legallyMarriedChanged || partnerAsDependentChanged
  }

  const getSuccessMessage = (): string => {
    return intl.formatMessage({ defaultMessage: "Account settings have been saved." })
  }

  const request = (userInfo: Record<string, any>) =>
    carrotClient.updateUser({
      ...userInfo,
      state: userInfo.state?.value ?? userInfo.state,
      internationalCountry: userInfo.internationalCountry?.value
    })

  const onSuccess = async () => {
    refreshUserInfo()
  }

  // Use regular useMutation when we don't want to show the toast
  const mutationWithoutToast = useMutation({
    mutationFn: request,
    onMutate: () => {
      // Notify that save has started (hide any existing alert panel)
      onSaveStarted?.()
      // Show loading toast
      toast.loading("Saving...", { id: "loading" })
    },
    onSuccess: async (data, variables) => {
      onSuccess()
      // Dismiss loading toast
      toast.dismiss("loading")
      // Check if partner fields were updated and notify parent
      if (hasPartnerFieldsChanged(variables)) {
        // Refetch both partner eligibility queries to ensure fresh data
        await queryClient.refetchQueries({ queryKey: ["partnerEligibility"] })
        await queryClient.refetchQueries({ queryKey: ["memberIdCardDetails"] })
        onPartnerFieldsUpdated?.()
      }
    },
    onError: () => {
      toast.dismiss("loading")
    },
    retry: false
  })

  // Create a custom mutation that shows toast and handles partner fields callback
  const mutationWithToast = useMutation({
    mutationFn: request,
    onMutate: () => {
      // Notify that save has started (hide any existing alert panel)
      onSaveStarted?.()
      // Show loading toast
      toast.loading("Saving...", { id: "loading" })
    },
    onSuccess: async (data, variables) => {
      onSuccess()
      // Show success toast
      const message = getSuccessMessage()
      successToast(message)
      toast.dismiss("loading")

      // Check if partner fields were updated and notify parent
      if (hasPartnerFieldsChanged(variables)) {
        // Refetch partner eligibility query and wait for it to complete
        await queryClient.refetchQueries({ queryKey: ["partnerEligibility"] })
        await queryClient.refetchQueries({ queryKey: ["memberIdCardDetails"] })
        onPartnerFieldsUpdated?.()
      }
    },
    onError: () => {
      toast.dismiss("loading")
    },
    retry: false
  })

  // Return the appropriate mutation based on whether we want to show the toast
  return {
    ...mutationWithToast,
    error: mutationWithToast.error || mutationWithoutToast.error,
    mutate: (userInfo: Record<string, any>) => {
      const showToast = shouldShowToast(userInfo)

      if (showToast) {
        mutationWithToast.mutate(userInfo)
      } else {
        mutationWithoutToast.mutate(userInfo)
      }
    },
    mutateAsync: async (userInfo: Record<string, any>) => {
      const showToast = shouldShowToast(userInfo)

      if (showToast) {
        return mutationWithToast.mutateAsync(userInfo)
      } else {
        return mutationWithoutToast.mutateAsync(userInfo)
      }
    }
  } as UseMutationResult
}
